/**
 * The console's chrome, replicated for a page that has no console in it.
 *
 * Every mockup on this page is built from these five pieces rather than from
 * one-off markup per figure, and that is what keeps the page honest as much as
 * short: a badge means the same thing in the fleet board as in the evidence
 * chain because it is the same component, reading the same tokens the console
 * reads.
 *
 * These are replicas, not imports. `web/src/components` is a live operator tool
 * with its own props, its own i18n and its own tests; pulling it in would couple
 * a marketing page to the console's refactors and put the console's bundle at
 * risk for nothing. The one thing genuinely shared is `tokens.css` — the
 * palette, where the contrast arithmetic was done.
 */
import type { ReactNode } from 'react'

export type Tone = 'neutral' | 'accent' | 'ok' | 'warn' | 'err' | 'info' | 'unknown'

/** A card. The console's `.k-panel`, with the same two variants. */
export function Panel({
  title,
  actions,
  children,
  framed = false,
  flush = false,
  className,
}: {
  title?: ReactNode
  actions?: ReactNode
  children: ReactNode
  framed?: boolean
  flush?: boolean
  className?: string
}) {
  return (
    <div className={`kc-panel ${framed ? 'kc-panel--framed' : ''} ${className ?? ''}`}>
      {title !== undefined ? (
        <div className="kc-panel__head">
          <span className="kc-panel__title">{title}</span>
          {actions ? <span className="kc-panel__actions">{actions}</span> : null}
        </div>
      ) : null}
      <div className={`kc-panel__body ${flush ? 'kc-panel__body--flush' : ''}`}>{children}</div>
    </div>
  )
}

/**
 * A state, in a word and a colour.
 *
 * The word is never optional. Colour is a second channel here, not the only
 * one — which is also why `unknown` has its own hue rather than a grey.
 */
export function Badge({
  tone = 'neutral',
  mono = false,
  children,
}: {
  tone?: Tone
  /** Machine values are mono. Ids, codes and tokens pass `mono`; states do not. */
  mono?: boolean
  children: ReactNode
}) {
  return <span className={`kc-badge kc-badge--${tone} ${mono ? 'k-mono' : ''}`}>{children}</span>
}

/** A dismissible-looking filter pill. Static here; the console's is not. */
export function Chip({ children, on = false }: { children: ReactNode; on?: boolean }) {
  return <span className={`kc-chip ${on ? 'kc-chip--on' : ''}`}>{children}</span>
}

/**
 * A terminal tile: title bar, status, and a canvas of fixed-width text.
 *
 * `aria-hidden` is not used on the transcript — the lines are real content and a
 * reader using a screen reader should be able to read what the product prints.
 */
export function Pane({
  title,
  status,
  children,
  active = false,
  className,
}: {
  title: string
  status?: ReactNode
  children: ReactNode
  active?: boolean
  className?: string
}) {
  return (
    <div className={`kc-pane ${active ? 'kc-pane--active' : ''} ${className ?? ''}`}>
      <div className="kc-pane__head">
        <span className="kc-pane__title k-mono">{title}</span>
        {status ? <span className="kc-pane__status">{status}</span> : null}
      </div>
      <div className="kc-pane__canvas k-mono">{children}</div>
    </div>
  )
}

/** One labelled row with a meter beside it. The fleet board is a stack of these. */
export function MeterRow({
  label,
  value,
  children,
}: {
  label: ReactNode
  /** Rendered to the right of the label — the reading, or the word for unmeasured. */
  value: ReactNode
  /** The meter itself, so the caller decides tone and delay. */
  children: ReactNode
}) {
  return (
    <div className="kc-meterrow">
      <span className="kc-meterrow__label">{label}</span>
      <span className="kc-meterrow__value k-mono">{value}</span>
      <span className="kc-meterrow__bar">{children}</span>
    </div>
  )
}
